/**
 * @fileoverview `EventMiniMap` (WEB-716) — the graticule mini-map that mounts
 * as a satellite section of `EventDecisionPanel`'s `children` slot.
 *
 * Design source: `docs/design-system/event-v2-m3-panel-satellite-specs.md` §1,
 * measured in real Chromium off the Claude Design bundle. Every geometry
 * number, stroke width and dash pattern below is authored there, not eyeballed.
 *
 * ## What this is, and deliberately is not
 *
 * A **decorative, explicitly not-to-scale** locator frame: a radial wash, an
 * 18px graticule masked to a soft vignette, three overshooting road strokes, a
 * dashed category thread, a pinned venue dot with an accuracy ring, and up to
 * two satellite dots. It renders **zero** network requests — no Google Maps
 * SDK, no `@googlemaps/js-api-loader`, no tile or static-map URL, no remote
 * `<img>` (spec §1.7). `DiscoverMapView` lazy-loads the Maps SDK precisely so
 * pages that don't need it never pay for it, and the event panel does not need
 * it.
 *
 * `organisms/NeighborhoodMapInset/projection.ts` carries a real
 * equirectangular `latLngToSvg`. It is deliberately **not** used here. Reach
 * for it if a future issue makes this block honest-geographic; do not
 * half-adopt it now.
 *
 * ## Satellite placement is a COUNT, not a projection (spec §1.5)
 *
 * The issue's prose asks for satellite positions "derived from the venue's own
 * coordinates plus the transit entries". The data to do that does not exist:
 * `transitStops` is `string[]` — names only, no coordinates — and `hasParking`
 * is a boolean (`types/eventDetail.ts:66,72`). The only coordinate-bearing path
 * is the lazy, optional Places merge, which the panel must not block on.
 *
 * So the satellites sit at the design's own fixed reference coordinates, and
 * the only data-driven quantity is **how many** render (0, 1 or 2) — one per
 * getting-there entry the panel's transit rows (WEB-718) show directly beneath.
 * That one-to-one correspondence is the consistency that is actually visible on
 * screen. A synthesised bearing or a distance-scaled radius would imply survey
 * accuracy this block does not have and the data cannot support, so neither is
 * derived here — nor should either be added later without real coordinates.
 *
 * ## Colour
 *
 * No hex literal appears in this file. The six `--evt-map-*` tokens are new to
 * this milestone and are declared in `styles/tokens/color.css` + `dark.css` for both themes
 * (spec §4). Category colour arrives as a caller-resolved `chroma` prop —
 * leaf components never call `getCategoryChroma` themselves, per the M1/M2
 * convention this file follows.
 *
 * The design's `--teal` (venue dot, accuracy ring, leader lines, the `N` glyph)
 * maps to `--clt-skyline-glow-fg`, which resolves to the spec's measured
 * `#4FC4C4` in dark and a higher-contrast `#0A6363` in light. Spec §4 names no
 * repo token for teal; this one is the M3-wide choice, already shipped by
 * `GettingThereCard` and by WEB-718's transit rows in the same card — three
 * satellites landing three different teals in one panel is the drift it avoids.
 *
 * ## Why `'use client'` on a component with no hooks
 *
 * Not decoration, and not cargo-culted from `RunOfDatesStrip` (which needs it
 * for `useId`). This file imports the **value** `PANEL_SECTION_CLASS` from
 * `EventDecisionPanel`, which is itself a `'use client'` module. A named export
 * of a client module, read from the RSC graph, is not the string — Next hands
 * back a client-reference proxy (`typeof === 'function'`). `cn` is
 * `twMerge(clsx(...))`, and clsx returns `''` for a function, so it would not
 * throw: the panel section rule would be **silently dropped**, and the map and
 * the venue snippet below it would run together with no separator.
 *
 * That is exactly the shape the M5 cutover produces — `EventSpineTemplate` is a
 * Server Component, so a satellite passed through `EventDecisionPanel`'s
 * `children` is evaluated in the server graph. Neither jsdom nor Storybook can
 * catch it (both resolve the plain string), so the boundary is stated here
 * instead. The alternative — hardcoding the border classes — is precisely what
 * `PANEL_SECTION_CLASS` was exported to prevent.
 */
'use client';

import type { CSSProperties, ReactElement } from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { PANEL_SECTION_CLASS } from '@/components/organisms/EventDecisionPanel';
import { EVT_MICRO_LABEL_DT_CLASS } from '@/lib/eventV2MicroLabel';
import { resolveNeighborhoodIdentity } from '@/lib/neighborhoods/clt-slugs';
import type { CategoryChroma } from '@/utils/categoryColors';

/**
 * The venue fields this block reads, declared structurally.
 *
 * `latitude` / `longitude` ride on the wire (`events-legacy.ts:2428-2459`,
 * extracted from PostGIS) but are **not declared** on the generated OpenAPI
 * `Location` schema that types `EventDetailV2['location']`. Declaring the read
 * shape here — all fields optional — means the generated `Location`, the
 * hand-written `EventDetailLocation`, and a bare literal are all structurally
 * assignable, and the coordinates can be read without `any` and without an
 * assertion. Same narrow-read posture as `EventSpineTemplate`'s
 * `SpineVenueLocation` (spec §1.6).
 */
export interface EventMiniMapVenue {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  neighborhood?: string | null;
}

/**
 * The two chroma channels the map paints with.
 *
 * `ModuleRowChroma` (the shared narrow type used by `RunOfDatesStrip` and the
 * M2 module rows) carries `catInk` / `catSoft` / `catLine` but **not** the base
 * hue `cat`, which the category satellite dot needs. `Pick` keeps this tied to
 * `CategoryChroma` — the object `getCategoryChroma(slug, theme)` returns is
 * assignable as-is.
 */
export type EventMiniMapChroma = Pick<CategoryChroma, 'cat' | 'catLine'>;

/** Which palette a satellite dot paints itself with. */
export type EventMiniMapSatelliteTint = 'neutral' | 'category';

export interface EventMiniMapSatellite {
  /** viewBox-space centre. Fixed reference layout — never derived from data. */
  cx: number;
  cy: number;
  tint: EventMiniMapSatelliteTint;
}

export interface EventMiniMapGeometry {
  satellites: readonly EventMiniMapSatellite[];
  /**
   * `address · neighborhood`, in **source casing**. C15 leaves this mono label
   * in source case (no CSS `uppercase`), so the accessible string is the
   * payload. `null` when the venue carries neither field.
   */
  label: string | null;
}

/* ── The design's fixed reference layout (spec §1.3) ─────────────────────── */

/** `viewBox="0 0 400 200"` with `preserveAspectRatio="none"`. */
const VIEWBOX = '0 0 400 200';

/**
 * Fixed `height`, not an aspect ratio — identical at every viewport (spec §0).
 *
 * Applied as an inline style rather than an `h-[200px]` class **on purpose**: a
 * Tailwind arbitrary value cannot read a TS constant, so a class would leave
 * this export free to drift from what actually renders. Inline keeps the
 * exported number the single source of truth for the block's height.
 */
export const EVENT_MINI_MAP_HEIGHT_PX = 200;

/** The venue is pinned dead centre of the viewBox. Always. */
const VENUE_CX = 200;
const VENUE_CY = 100;
const VENUE_DOT_R = 9;
const VENUE_RING_R = 28;
const VENUE_RING_OPACITY = 0.45;

const SATELLITE_R = 6;
const LEADER_OPACITY = 0.5;

/** At most two satellites — the design draws two slots and no more. */
const MAX_SATELLITES = 2;

/**
 * Slot order is load-bearing: the FIRST getting-there entry takes the neutral
 * dot, the second takes the category-tinted one (spec §1.5).
 */
const SATELLITE_SLOTS: readonly EventMiniMapSatellite[] = [
  { cx: 286, cy: 52, tint: 'neutral' },
  { cx: 118, cy: 66, tint: 'category' },
];

/**
 * Three road strokes. Each overshoots the viewBox by 10 units on both ends so
 * no stroke cap is ever visible inside the frame.
 */
const ROADS: readonly { readonly d: string; readonly width: number }[] = [
  { d: 'M -10 150 L 410 132', width: 6 },
  { d: 'M -10 70 L 410 56', width: 4 },
  { d: 'M 120 -10 L 108 210', width: 4 },
];

/** The category-tinted dashed thread — a 3-point polyline crossing the frame. */
const CATEGORY_THREAD_D = 'M 40 210 L 130 110 L 230 30';
const CATEGORY_THREAD_WIDTH = 1.5;
const CATEGORY_THREAD_DASH = '6 3';

const LEADER_DASH = '2 4';

/** Radial ground: `--evt-map-a` at the centre fading to `--evt-map-b` at the rim. */
const RADIAL_WASH =
  'radial-gradient(ellipse 60% 80% at 50% 50%, var(--evt-map-a) 0%, var(--evt-map-b) 100%)';

/**
 * The 18px graticule, faded past 82% of the radius.
 *
 * `-webkit-mask-image` is not optional — the design bundle omits it, but Safari
 * (all versions LOCI supports) still requires the prefixed property, and
 * without it the grid renders as a hard-edged full-bleed lattice instead of a
 * vignette (spec §1.2's own note).
 */
const GRID_MASK = 'radial-gradient(80% 80%, black 30%, transparent 82%)';

/**
 * Exported solely so the component test can assert the `-webkit-` companion is
 * authored. Nothing at runtime can: Chromium treats `-webkit-mask-image` as an
 * **alias** of `mask-image`, collapsing them in the CSSOM as well as in
 * computed style, so neither `getComputedStyle()` nor the serialized `style`
 * attribute can tell "both authored" from "only `mask-image` authored". A
 * Chromium-only Playwright lane therefore cannot guard this at all, and the
 * check has to happen on the authored object.
 */
export const EVENT_MINI_MAP_GRID_WASH_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(var(--evt-map-grid) 1px, transparent 1px), ' +
    'linear-gradient(90deg, var(--evt-map-grid) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};

/**
 * Address label type: 10px / 400 / 0.1em / mono, source case (C15; spec §1.4).
 * The scale module names this treatment after its first consumer (the panel's
 * `<dt>` row key); the map label is that same measured treatment, so it
 * consumes the shared constant rather than restating the four numbers.
 */
const ADDRESS_LABEL_CLASS = EVT_MICRO_LABEL_DT_CLASS;

/* ── Pure resolution ─────────────────────────────────────────────────────── */

function trimmedOrNull(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Canonical neighborhood name. A known CLT slug prints its display name
 * (`noda` → `NoDa`); anything else is title-cased so a raw kebab slug never
 * prints as-is. An unrecognized name is still a name — omitting it hid the
 * fixture district on the address line.
 */
function neighborhoodDisplayName(value: string | null | undefined): string | null {
  const trimmed = trimmedOrNull(value);
  if (trimmed == null) return null;
  const name = resolveNeighborhoodIdentity(trimmed).displayName.trim();
  return name.length > 0 ? name : null;
}

/**
 * Whether the venue carries a usable coordinate pair.
 *
 * Finite and in-range only. Nothing cleverer: a plausible-but-wrong sentinel
 * rule (rejecting `0,0`, say) would be an invented policy, and out-of-range
 * values are the only ones provably not coordinates.
 */
function hasRenderableCoordinates(venue: EventMiniMapVenue): boolean {
  const { latitude, longitude } = venue;
  return (
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    Math.abs(latitude) <= 90 &&
    typeof longitude === 'number' &&
    Number.isFinite(longitude) &&
    Math.abs(longitude) <= 180
  );
}

/**
 * Takes the first `count` satellite slots, capped at two.
 *
 * `count` is the number of getting-there options the shared builder
 * (`lib/gettingThere/buildGettingThereOptions.ts`) produced for this venue,
 * resolved by the caller so the map and WEB-718's rows read one derivation.
 * Zero (or a nonsense value) yields zero satellites and zero leader lines —
 * the venue dot, ring, roads, grid and category thread still render (spec §1.5).
 */
function resolveSatellites(count: number): readonly EventMiniMapSatellite[] {
  if (!Number.isFinite(count) || count <= 0) return [];
  return SATELLITE_SLOTS.slice(0, Math.min(Math.floor(count), MAX_SATELLITES));
}

/**
 * Resolves the block's full render geometry, or `null` when the venue has no
 * usable coordinates — in which case the whole block, separator rule included,
 * renders nothing (spec §1.6). Pure; exported so the gate is testable without
 * a DOM.
 */
export function resolveEventMiniMapGeometry(
  venue: EventMiniMapVenue | null | undefined,
  gettingThereCount: number,
): EventMiniMapGeometry | null {
  if (!venue || !hasRenderableCoordinates(venue)) {
    return null;
  }
  const parts = [trimmedOrNull(venue.address), neighborhoodDisplayName(venue.neighborhood)].filter(
    (part): part is string => part !== null,
  );
  return {
    satellites: resolveSatellites(gettingThereCount),
    label: parts.length > 0 ? parts.join(' · ') : null,
  };
}

/* ── Component ───────────────────────────────────────────────────────────── */

export interface EventMiniMapProps {
  /**
   * The event's venue. Accepts the generated `Location`, the hand-written
   * `EventDetailLocation`, or `null` — see {@link EventMiniMapVenue}.
   */
  venue: EventMiniMapVenue | null | undefined;
  /**
   * How many getting-there options the shared builder produced for this venue
   * — i.e. how many rows WEB-718 renders beneath. Resolved by the caller (the
   * M5 composer) from `buildGettingThereOptionsFromLocation`, so the dots and
   * the rows can never disagree. Only 0/1/2 are distinguishable; larger values
   * clamp to 2.
   */
  gettingThereCount: number;
  /** Caller-resolved category chroma (`getCategoryChroma`) — never derived here. */
  chroma: EventMiniMapChroma;
  className?: string;
}

/**
 * `EventMiniMap` — a self-contained panel section carrying
 * `PANEL_SECTION_CLASS` (WEB-738) so it mounts bare into
 * `EventDecisionPanel`'s `children` slot as its own full-bleed section. The
 * trailing rule is **not** suppressed here: the card root's
 * `[&>*:last-child]:border-b-0` handles "last section has no rule" structurally,
 * across every combination of present and absent satellites.
 *
 * Returns `null` when the venue has no usable coordinates.
 */
export function EventMiniMap({
  venue,
  gettingThereCount,
  chroma,
  className,
}: EventMiniMapProps): ReactElement | null {
  const geometry = resolveEventMiniMapGeometry(venue, gettingThereCount);
  if (!geometry) {
    return null;
  }

  const satelliteFill = (tint: EventMiniMapSatelliteTint): string =>
    tint === 'category' ? chroma.cat : 'var(--evt-map-dot)';

  return (
    <div
      data-testid="event-mini-map"
      className={cn(PANEL_SECTION_CLASS, 'relative', className)}
      // Preflight's `box-sizing: border-box` means the measured box stays
      // exactly 200px whether or not PANEL_SECTION_CLASS's 1px rule is present
      // — which is what makes "exactly 200px at all three viewports" hold at
      // every panel width.
      style={{ background: RADIAL_WASH, height: EVENT_MINI_MAP_HEIGHT_PX }}
    >
      <div aria-hidden="true" className="absolute inset-0" style={EVENT_MINI_MAP_GRID_WASH_STYLE} />

      <svg
        // Decorative in full: the only accessible content is the address label
        // below (spec §1.3). `focusable="false"` keeps legacy IE/Edge from
        // putting the <svg> in the tab order.
        aria-hidden="true"
        focusable="false"
        viewBox={VIEWBOX}
        // Stretches horizontally at tablet/mobile. Intended by the design —
        // the frame is decorative, so a non-uniform scale costs nothing and
        // keeps the composition edge-to-edge at every panel width.
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        data-testid="event-mini-map-svg"
      >
        {ROADS.map((road) => (
          <path
            key={road.d}
            d={road.d}
            fill="none"
            stroke="var(--evt-map-road)"
            strokeWidth={road.width}
          />
        ))}

        <path
          d={CATEGORY_THREAD_D}
          fill="none"
          stroke={chroma.catLine}
          strokeWidth={CATEGORY_THREAD_WIDTH}
          strokeDasharray={CATEGORY_THREAD_DASH}
          data-testid="event-mini-map-thread"
        />

        {/* Leaders are drawn per-element rather than under a shared <g>:
            presentation attributes on a group do not inherit uniformly (an
            `opacity` there is GROUP opacity, a different compositing rule),
            and stating them per line removes that class of surprise. */}
        {geometry.satellites.map((satellite) => (
          <line
            key={`leader-${satellite.cx}-${satellite.cy}`}
            x1={VENUE_CX}
            y1={VENUE_CY}
            x2={satellite.cx}
            y2={satellite.cy}
            stroke="var(--clt-skyline-glow-fg)"
            strokeWidth={1}
            strokeDasharray={LEADER_DASH}
            opacity={LEADER_OPACITY}
            data-testid="event-mini-map-leader"
          />
        ))}

        {geometry.satellites.map((satellite) => (
          <circle
            key={`satellite-${satellite.cx}-${satellite.cy}`}
            cx={satellite.cx}
            cy={satellite.cy}
            r={SATELLITE_R}
            fill={satelliteFill(satellite.tint)}
            stroke={satelliteFill(satellite.tint)}
            strokeWidth={2}
            data-testid="event-mini-map-satellite"
            data-tint={satellite.tint}
          />
        ))}

        <circle
          cx={VENUE_CX}
          cy={VENUE_CY}
          r={VENUE_RING_R}
          fill="none"
          stroke="var(--clt-skyline-glow-fg)"
          strokeWidth={1}
          opacity={VENUE_RING_OPACITY}
          data-testid="event-mini-map-accuracy-ring"
        />
        <circle
          cx={VENUE_CX}
          cy={VENUE_CY}
          r={VENUE_DOT_R}
          fill="var(--clt-skyline-glow-fg)"
          stroke="var(--clt-skyline-glow-fg)"
          strokeWidth={2}
          data-testid="event-mini-map-venue-dot"
        />
      </svg>

      {/* North marker. Decorative — a bare "N" announced on its own is noise,
          and the map it orients is itself aria-hidden.

          Fill: the design's `--head` is the page ground at ~92% (dark) / ~94%
          (light) alpha. The repo has no translucent page-ground token and
          authoring one would put a translucent-white literal in `:root`, which
          the DPL-41 parity sweep correctly rejects — so this uses the opaque
          ground itself. `--loci-color-background` rather than its `--bg` alias:
          `--bg` is re-scoped by the nav strip, and this chip's colour must not
          depend on where it is mounted. */}
      <div
        aria-hidden="true"
        data-testid="event-mini-map-north"
        className="absolute right-3 top-3 grid h-[30px] w-[30px] place-items-center rounded-full border border-ds-border bg-loci-background text-micro text-clt-skyline-glow-fg font-mono"
      >
        N
      </div>

      {geometry.label !== null && (
        <p
          data-testid="event-mini-map-address"
          // `right-[14px]` + `truncate` bound the label to the frame. The
          // design's own fixture is one short line with no width constraint;
          // a long real address would otherwise bleed past the card at the
          // 354px mobile inner width.
          className={cn(
            'absolute bottom-3 left-[14px] right-[14px] truncate text-evt-map-ink',
            ADDRESS_LABEL_CLASS,
          )}
        >
          {geometry.label}
        </p>
      )}
    </div>
  );
}
